import React, { forwardRef } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { TriangleAlert, CircleX } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./Button";

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  message: string | React.ReactNode;
  variant?: "warning" | "danger" | "info";
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  /**
   * Disables the CONFIRM button only (never the Cancel button).
   *
   * `loading` freezes BOTH buttons (in-flight, non-interactive state). This
   * prop gates only the confirm action — e.g. a name-confirmation gate that
   * must not trap the user inside the dialog, since Cancel stays live.
   */
  confirmDisabled?: boolean;
}

const variantConfig = {
  warning: { icon: TriangleAlert, iconBg: "bg-warning-bg text-warning", confirmVariant: "default" as const },
  danger: { icon: CircleX, iconBg: "bg-error-bg text-error", confirmVariant: "destructive" as const },
  info: { icon: TriangleAlert, iconBg: "bg-accent/10 text-accent", confirmVariant: "default" as const },
};

const ConfirmDialog = forwardRef<HTMLDivElement, ConfirmDialogProps>(
  ({ open, onOpenChange, onConfirm, onCancel, title, message, variant = "warning", confirmLabel = "Confirmar", cancelLabel = "Cancelar", loading = false, confirmDisabled = false }, ref) => {
    const config = variantConfig[variant];
    const Icon = config.icon;
    return (
      <Dialog.Root open={open} onOpenChange={onOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-[1040] bg-black/40 backdrop-blur-sm" />
          <Dialog.Content ref={ref} className="fixed left-1/2 top-1/2 z-[1050] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-card p-6 shadow-lg focus:outline-none">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className={cn("flex h-14 w-14 items-center justify-center rounded-full", config.iconBg)}>
                <Icon className="h-7 w-7" />
              </div>
              <Dialog.Title className="text-lg font-semibold text-foreground">{title}</Dialog.Title>
              <div className="text-sm text-muted-foreground" role="description">{message}</div>
              <div className="mt-2 flex w-full items-center justify-center gap-3">
                <Button variant="outline" onClick={onCancel} disabled={loading}>{cancelLabel}</Button>
                <Button variant={config.confirmVariant} onClick={onConfirm} disabled={loading || confirmDisabled}>{confirmLabel}</Button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  },
);
ConfirmDialog.displayName = "ConfirmDialog";

export { ConfirmDialog };
